import { AI_INCIDENT_TYPES } from "./adr.constants"

import type { AdrIncidentRecord } from "@/api/useIncidents.types"
import type {
  AdrIncident,
  AdrIncidentDTO,
  AdrState,
  InstanceInfoDTO,
  Priority,
  TypeAiDTO
} from "."
import type { Severity } from "../vulnerability"

export const getIncidentDTO = (record: AdrIncidentRecord) => record.incident

export const getGeneralSectionDTO = (incident: AdrIncidentDTO) =>
  incident.general

export const getIdDTO = (incident: AdrIncidentDTO) =>
  getGeneralSectionDTO(incident).id

export const getTypeDTO = (incident: AdrIncidentDTO) =>
  getGeneralSectionDTO(incident).type

export const getEnvironmentDTO = (incident: AdrIncidentDTO) =>
  getGeneralSectionDTO(incident).environment

export const getSeverityByPriority = (priority: Priority): Severity => {
  switch (priority) {
    case "urgent":
      return "critical"
    case "warning":
      return "medium"
    case "notice":
      return "low"
    default:
      return "unknown"
  }
}

export const getTechnicalRecords = (record: AdrIncident) =>
  record.technicalId || []

export const getTechnicalRecord = (record: AdrIncident, index = 0) =>
  getTechnicalRecords(record)[index]

export const getTechnicalId = (record: AdrIncident) =>
  getTechnicalRecord(record)?.id

export const getTechnicalType = (record: AdrIncident) =>
  getTechnicalRecord(record)?.type

export const canShowVulnerableFunctions = (record?: AdrIncident) => {
  if (!record) return false

  return record.type === "POTENTIAL_EXPLOIT_CVE"
}

export const showRuntimeEvidence = (record: AdrIncident) => {
  switch (record.type) {
    case "EXPOSURE_TO_NEWLY_PUBLISHED_CVE":
    case "POTENTIAL_EXPLOIT_CVE":
      return true

    default:
      return false
  }
}

export const isAiIncidentType = (type: string): type is TypeAiDTO =>
  AI_INCIDENT_TYPES.some((aiType) => aiType === type)

export const getEnforcement = (record: AdrIncident) => record.enforcement

export const isBlocked = (record: AdrIncident) =>
  getEnforcement(record)?.action === "blocked"

export const showCallStackEvidence = (record: AdrIncident) => {
  if (isAiIncidentType(record.type)) return true

  switch (record.type) {
    case "CODE_INJECTION":
    case "OS_COMMAND_INJECTION":
    case "UNSAFE_DESERIALIZATION":
    case "EXEC_MEMORY_ANOMALOUS_JIT":
      return true

    default:
      return false
  }
}

export const canShowEvidence = (record?: AdrIncident) => {
  if (!record) return false

  return showCallStackEvidence(record) || showRuntimeEvidence(record)
}

export const isCWE = (record: AdrIncident) => getTechnicalType(record) === "CWE"

export const isMitre = (record: AdrIncident) =>
  getTechnicalType(record) === "MITRE"

export const isCVE = (record: AdrIncident) => getTechnicalType(record) === "CVE"

export const getVulnerabilities = (record: AdrIncident) =>
  record.what.vulnerabilities || []

export const hasVulnerabilities = (record: AdrIncident) =>
  !!getVulnerabilities(record).length

export const getCountCve = (record: AdrIncident) => {
  if (!isCVE(record)) return 0

  return getVulnerabilities(record).length
}

export const isMultipleCVE = (record: AdrIncident) => getCountCve(record) > 1

export const getInstanceType = (record: AdrIncident): string =>
  record.where?.instanceType ?? ""

// parseInstanceInfo decodes where.instanceInfo (a protojson string of
// kortexevents.InstanceInfo) into a structured object, or null when absent or
// unparseable. Older incidents send "" or "{}".
export const parseInstanceInfo = (
  record: AdrIncident
): InstanceInfoDTO | null => {
  const raw = record.where?.instanceInfo
  if (typeof raw !== "string" || raw === "" || raw === "{}") return null
  try {
    return JSON.parse(raw) as InstanceInfoDTO
  } catch {
    return null
  }
}

// isVM reports whether the incident is host-scoped (a VM, an ec2 instance, or a
// container running directly on a VM) rather than a k8s workload. The location
// UI uses this to drop k8s-only fields (namespace/pod). It honours the legacy
// record.isVM flag and the newer where.instanceType signal.
export const isVM = (record: AdrIncident) =>
  Boolean(record.isVM) ||
  record.where?.instanceType !== "INSTANCE_TYPE_K8S_WORKLOAD"

export const isLearningState = (state: AdrState | undefined) =>
  state === "learning"

export const isPrerequisitesNotMetState = (state: AdrState | undefined) =>
  state === "prerequisites_not_met"

export const isPrerequisitesDegradedState = (state: AdrState | undefined) =>
  state === "prerequisites_degraded"

export const isActiveState = (state: AdrState | undefined) => state === "active"
